import { create } from 'zustand';
import { i18n } from '../i18n';
import { getAbsencePolicy } from '../api/generated/absence-policy/absence-policy';
import type { FacilityFlowCoreModulesPlanningDtosAbsencePoliciesAbsencePolicyDto } from '../api/generated/models';
import type { AbsencePolicyDto, CarryOverMode, VacationYearMode } from '../types';
import { useToastStore } from './useToastStore';

interface AbsencePolicyState {
  policy: AbsencePolicyDto | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
  fetchPolicy: () => Promise<void>;
  updatePolicy: (data: AbsencePolicyDto) => Promise<AbsencePolicyDto | null>;
}

const api = getAbsencePolicy();

const t = (key: string) => i18n.t(key, { ns: 'absenceSettings' }) as string;

const toPolicy = (
  dto: FacilityFlowCoreModulesPlanningDtosAbsencePoliciesAbsencePolicyDto,
): AbsencePolicyDto => ({
  vacationYearMode: (dto.vacationYearMode as VacationYearMode) ?? 'CalendarYear',
  vacationYearStartMonth: dto.vacationYearStartMonth ?? 1,
  vacationYearStartDay: dto.vacationYearStartDay ?? 1,
  defaultAnnualVacationDays: dto.defaultAnnualVacationDays ?? 25,
  carryOverMode: (dto.carryOverMode as CarryOverMode) ?? 'ExpiresAt',
  carryOverExpiryMonth: dto.carryOverExpiryMonth ?? 3,
  carryOverExpiryDay: dto.carryOverExpiryDay ?? 31,
  sickCertificateRequiredFromDays: dto.sickCertificateRequiredFromDays ?? null,
  updatedAt: dto.updatedAt ?? null,
  overtimeStartDate: dto.overtimeStartDate ?? null,
  roundVacationDaysToWholeDays: dto.roundVacationDaysToWholeDays ?? false,
});

export const useAbsencePolicyStore = create<AbsencePolicyState>((set) => ({
  policy: null,
  loading: false,
  saving: false,
  error: null,

  fetchPolicy: async () => {
    set({ loading: true, error: null });
    try {
      const response = await api.getApiV1AbsencePolicy();
      set({ policy: toPolicy(response), loading: false });
    } catch {
      set({ error: 'errors.fetchFailed', loading: false });
    }
  },

  updatePolicy: async (data) => {
    set({ saving: true, error: null });
    try {
      const response = await api.putApiV1AbsencePolicy({
        vacationYearMode: data.vacationYearMode,
        vacationYearStartMonth: data.vacationYearStartMonth,
        vacationYearStartDay: data.vacationYearStartDay,
        defaultAnnualVacationDays: data.defaultAnnualVacationDays,
        carryOverMode: data.carryOverMode,
        carryOverExpiryMonth: data.carryOverExpiryMonth,
        carryOverExpiryDay: data.carryOverExpiryDay,
        sickCertificateRequiredFromDays: data.sickCertificateRequiredFromDays ?? null,
        overtimeStartDate: data.overtimeStartDate ?? null,
        roundVacationDaysToWholeDays: data.roundVacationDaysToWholeDays,
      });

      const policy = toPolicy(response);
      set({ policy, saving: false });
      useToastStore.getState().addToast(t('messages.saved'), 'success');
      return policy;
    } catch (error) {
      set({ saving: false, error: 'errors.saveFailed' });
      throw error;
    }
  },
}));
